/**
 * Free-drawing activities on a <canvas> in stage coordinates:
 *
 *  - paint (PlayspacePaint.cs): an optional image to colour (paintImage, scaled
 *    by imageScale) and a toolbox: colours, brush sizes, eraser, clear. The
 *    slide ends when the player presses "Done".
 *  - simbolDraw (PlayspaceSimbolDraw.cs): the symbol(s) to trace are shown faded
 *    in the symbol's colour, with the guide points of the stroke being traced;
 *    the page is solved when every stroke is traced (TraceBoard).
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import type { SlideOf } from "@makeit/schema";
import { useActivity } from "../activity";
import { useStageScale } from "../drag";
import { mediaUrl } from "../../data/media";
import { usePlayer } from "../context";
import { t } from "../i18n";
import { STAGE_H, STAGE_W } from "../Stage";
import { reach, symbolBoxes, traceStrokes, type Box } from "../simbolTrace";

const PALETTE = ["#000000", "#ffffff", "#fb0000", "#fd9100", "#ffd400", "#39b54a", "#06aae2", "#2a4bd7", "#752992", "#ff6fb5", "#8b5a2b", "#9e9e9e"];
const SIZES = [12, 28, 56];

export function PaintSlide({ slide }: { slide: SlideOf<"paint"> }) {
  const { project } = usePlayer();
  const src = mediaUrl(slide.paintImage?.image, "image", project.id);
  const scale = Math.max(0.1, slide.imageScale || 1);
  return (
    <DrawBoard overlay={src ? <img className="paint-image" src={src} alt="" draggable={false} style={{ transform: `scale(${scale})` }} /> : null} overlayOnTop />
  );
}

export function SimbolDrawSlide({ slide }: { slide: SlideOf<"simbolDraw"> }) {
  const { project } = usePlayer();
  const elements = slide.drawElements.filter((e) => e.simbol?.image);
  const strokes = useMemo(() => traceStrokes(elements.map((e) => e.simbol)), [slide]); // eslint-disable-line react-hooks/exhaustive-deps
  const boxes = useMemo(() => symbolBoxes(elements.length), [elements.length]);
  const pictures = (
    <>
      {elements.map((e, i) => {
        // the symbol pictures are white shapes, tinted with the symbol colour (PlayspaceSimbolDraw)
        const url = `url("${mediaUrl(e.simbol!.image, "image", project.id) ?? ""}")`;
        const b = boxes[i];
        return <div key={e.id} className="simbol-image" style={{ left: b.left, top: b.top, width: b.size, height: b.size, background: e.simbol!.color || "#5DA357", opacity: 0.35, WebkitMaskImage: url, maskImage: url }} />;
      })}
    </>
  );
  // a symbol without recorded points can only be drawn freely
  if (!strokes.length) return <DrawBoard overlay={<div className="simbol-row">{pictures}</div>} />;
  return <TraceBoard strokes={strokes} boxes={boxes} pictures={pictures} />;
}

/**
 * Tracing (PlayspaceDrawSimbolController): start on the star of the stroke and
 * go through its points in order (the next three are shown); ink only shows
 * while tracing. Straying too far from the next point, or lifting the finger
 * before the end of the stroke, wipes the stroke out. When every stroke of
 * every symbol is traced the page is solved.
 */
function TraceBoard({ strokes, boxes, pictures }: { strokes: ReturnType<typeof traceStrokes>; boxes: Box[]; pictures: React.ReactNode }) {
  const activity = useActivity(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scale = useStageScale();
  const [stroke, setStroke] = useState(0); // the stroke being traced
  const [target, setTarget] = useState(0); // its next point (0 = not started)
  const live = useRef<{ target: number; last: { x: number; y: number }; snapshot: ImageData | null } | null>(null);
  // PlayspaceSimbolDraw: brush 110 with one symbol, 70 with two
  const brush = Math.round((boxes[0]?.size ?? 1000) * 0.1);
  const done = stroke >= strokes.length;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = STAGE_W;
    c.height = STAGE_H;
  }, []);

  const ctx = () => canvasRef.current?.getContext("2d") ?? null;
  const pos = (e: RPointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };
  const ink = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const g = ctx();
    if (!g) return;
    g.strokeStyle = "#000";
    g.lineWidth = brush;
    g.lineCap = "round";
    g.lineJoin = "round";
    g.beginPath();
    g.moveTo(a.x, a.y);
    g.lineTo(b.x, b.y);
    g.stroke();
  };
  const wipe = () => {
    const l = live.current;
    if (l?.snapshot) ctx()?.putImageData(l.snapshot, 0, 0);
    live.current = null;
    setTarget(0);
  };
  const finishStroke = () => {
    live.current = null;
    setTarget(0);
    const next = stroke + 1;
    setStroke(next);
    if (next >= strokes.length) activity.correct();
  };

  const down = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (done || activity.isDone()) return;
    const st = strokes[stroke];
    const box = boxes[st.symbol];
    const p = pos(e);
    const first = st.points[0];
    if (Math.hypot(p.x - first.x, p.y - first.y) > reach(box, false) * 1.4) return; // PlayspacePointItemController.OnBeginDrag: on the star only
    e.currentTarget.setPointerCapture(e.pointerId);
    const g = ctx();
    live.current = { target: 1, last: p, snapshot: g ? g.getImageData(0, 0, STAGE_W, STAGE_H) : null };
    ink(p, p);
    setTarget(1);
    if (st.points.length === 1) finishStroke();
  };
  const move = (e: RPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l) return;
    const st = strokes[stroke];
    const box = boxes[st.symbol];
    const p = pos(e);
    ink(l.last, p);
    l.last = p;
    const tp = st.points[l.target];
    const d = Math.hypot(p.x - tp.x, p.y - tp.y);
    if (d <= reach(box, tp.sub)) {
      l.target += 1;
      if (l.target >= st.points.length) finishStroke();
      else setTarget(l.target);
      return;
    }
    // PlayspacePointItemController.OnDrag: too far from the next point (twice the step) → start the stroke again
    const prev = st.points[l.target - 1];
    if (d > Math.max(2 * Math.hypot(tp.x - prev.x, tp.y - prev.y), reach(box, tp.sub) * 2.5)) wipe();
  };
  const up = () => {
    if (live.current) wipe(); // lifted before the end of the stroke
  };

  const st = done ? null : strokes[stroke];
  const shown = st ? st.points.map((p, i) => ({ p, i })).filter(({ i }) => (target === 0 ? i <= 3 : i >= target && i < target + 3)) : [];
  return (
    <div className="draw-board">
      <div className="draw-overlay">{pictures}</div>
      <svg className="trace-marks" width={STAGE_W} height={STAGE_H} aria-hidden>
        {shown.map(({ p, i }) =>
          i === 0 ? (
            <polygon key={i} className="trace-start" points={starPoints(p.x, p.y, 42, 18)} />
          ) : (
            <circle key={i} className={`trace-dot ${p.sub ? "sub" : ""} ${i === target ? "next" : ""}`} cx={p.x} cy={p.y} r={p.sub ? 12 : 22} />
          ),
        )}
      </svg>
      <canvas ref={canvasRef} className="draw-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
    </div>
  );
}

function starPoints(cx: number, cy: number, r: number, r2: number): string {
  return Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r2 : r;
    return `${cx + rr * Math.cos(a)},${cy + rr * Math.sin(a)}`;
  }).join(" ");
}

function DrawBoard({ overlay, overlayOnTop }: { overlay: React.ReactNode; overlayOnTop?: boolean }) {
  const activity = useActivity(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scale = useStageScale();
  const [color, setColor] = useState(PALETTE[2]);
  const [size, setSize] = useState(SIZES[1]);
  const [eraser, setEraser] = useState(false);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = STAGE_W;
    c.height = STAGE_H;
  }, []);

  const pos = (e: RPointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };
  const down = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (activity.isDone()) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    stroke(last.current, last.current);
  };
  const move = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    const p = pos(e);
    stroke(last.current, p);
    last.current = p;
  };
  const up = () => {
    drawing.current = false;
    last.current = null;
  };
  const stroke = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.globalCompositeOperation = eraser ? "destination-out" : "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = eraser ? size * 2 : size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };
  const clear = () => canvasRef.current?.getContext("2d")?.clearRect(0, 0, STAGE_W, STAGE_H);

  return (
    <div className="draw-board">
      {!overlayOnTop && <div className="draw-overlay">{overlay}</div>}
      <canvas ref={canvasRef} className="draw-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
      {overlayOnTop && <div className="draw-overlay on-top">{overlay}</div>}
      <div className="draw-toolbox">
        {PALETTE.map((c) => (
          <button key={c} className={`swatch ${c === color && !eraser ? "active" : ""}`} style={{ background: c }} onClick={() => { setColor(c); setEraser(false); }} aria-label={c} />
        ))}
        <span className="tool-sep" />
        {SIZES.map((s) => (
          <button key={s} className={`brush ${s === size ? "active" : ""}`} onClick={() => setSize(s)} aria-label={`brush ${s}`}>
            <span style={{ width: s / 2, height: s / 2 }} />
          </button>
        ))}
        <button className={`tool ${eraser ? "active" : ""}`} onClick={() => setEraser((e) => !e)} aria-label="eraser">◻</button>
        <button className="tool" onClick={clear} aria-label={t("clear")}>↺</button>
        <span className="tool-sep" />
        <button className="btn stage-btn" onClick={() => activity.done()}>{t("done")}</button>
      </div>
    </div>
  );
}
